import React, { useEffect, useRef } from "react";
import "../../scss/matrix_hero.scss";

/* ═══════════════ CHARACTERS POOL ═══════════════ */

const MATRIX_CHARS =
    "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ@#$%&*<>{}[]";

/* ═══════════════ CANVAS RAIN ═══════════════ */

interface RainProps {
    density: number;
}

const MatrixRainCanvas: React.FC<RainProps> = ({ density }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctxRaw = canvas.getContext("2d");
        if (!ctxRaw) return;
        const ctx: CanvasRenderingContext2D = ctxRaw;

        let w = 0,
            h = 0;
        let dpr = window.devicePixelRatio || 1;
        let columns = 0;
        let drops: number[] = [];
        let speeds: number[] = [];
        let brightness: number[] = [];
        let charIdx: number[] = [];
        let colActive: boolean[] = [];

        const FONT_SIZE = 14;
        const TAIL = 18;
        const chars = MATRIX_CHARS.split("");

        // Speed: visible drift
        const SP_MIN = 0.02;
        const SP_RANGE = 0.06;

        /** Enforce simultaneous-column cap equal to density * columns. */
        function activateCols() {
            const maxActive = Math.max(1, Math.floor(columns * density));
            let activeCount = 0;
            for (let i = 0; i < columns; i++) {
                if (colActive[i]) activeCount++;
            }
            // Fill up to cap from random inactive columns
            if (activeCount < maxActive) {
                const inactive: number[] = [];
                for (let i = 0; i < columns; i++) {
                    if (!colActive[i]) inactive.push(i);
                }
                // shuffle
                for (let j = inactive.length - 1; j > 0; j--) {
                    const k = Math.floor(Math.random() * (j + 1));
                    [inactive[j], inactive[k]] = [inactive[k], inactive[j]];
                }
                const toActivate = Math.min(
                    maxActive - activeCount,
                    inactive.length
                );
                for (let a = 0; a < toActivate; a++) {
                    colActive[inactive[a]] = true;
                    drops[inactive[a]] = Math.random() * -30;
                    speeds[inactive[a]] = SP_MIN + Math.random() * SP_RANGE;
                    brightness[inactive[a]] = 0.15 + Math.random() * 0.25;
                    charIdx[inactive[a]] = Math.floor(
                        Math.random() * chars.length
                    );
                }
            }
        }

        function initDrops() {
            columns = Math.ceil(w / FONT_SIZE);
            drops = [];
            speeds = [];
            brightness = [];
            charIdx = [];
            colActive = [];
            for (let i = 0; i < columns; i++) {
                drops.push(Math.random() * -40);
                speeds.push(SP_MIN + Math.random() * SP_RANGE);
                brightness.push(0.15 + Math.random() * 0.25);
                charIdx.push(Math.floor(Math.random() * chars.length));
                colActive.push(false);
            }
            activateCols();
        }

        const resize = () => {
            const parent = canvas.parentElement;
            if (!parent) return;
            dpr = window.devicePixelRatio || 1;
            w = parent.clientWidth;
            h = parent.clientHeight;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            initDrops();
        };
        resize();
        window.addEventListener("resize", resize);

        let animId: number;

        function draw() {
            ctx.clearRect(0, 0, w, h);
            ctx.font = `${FONT_SIZE}px "JetBrains Mono", monospace`;

            for (let i = 0; i < columns; i++) {
                if (!colActive[i]) continue;

                const x = i * FONT_SIZE;
                const headRow = Math.floor(drops[i]);
                const b = brightness[i];

                if (Math.random() < 0.02) {
                    charIdx[i] = Math.floor(Math.random() * chars.length);
                }

                for (let t = 0; t < TAIL; t++) {
                    const row = headRow - t;
                    const y = row * FONT_SIZE;
                    if (y < -FONT_SIZE || y > h) continue;

                    const ci =
                        ((charIdx[i] + t * 13 + row * 3) % chars.length +
                            chars.length) %
                        chars.length;
                    const char = chars[ci];

                    if (t === 0) {
                        ctx.fillStyle = `rgba(180, 255, 180, ${0.55 * b})`;
                    } else {
                        const fade = 1 - t / TAIL;
                        ctx.fillStyle = `rgba(105, 241, 61, ${fade * 0.35 * b})`;
                    }
                    ctx.fillText(char, x, y);
                }

                drops[i] += speeds[i];

                // Column finished — deactivate; activateCols will refill
                if (drops[i] > h / FONT_SIZE + TAIL) {
                    colActive[i] = false;
                }
            }

            // Refill so there are always density*columns active streams
            activateCols();

            // Central dark void — text readability
            const cx = w / 2;
            const cy = h / 2;
            const voidR = Math.min(w, h) * 0.38;
            const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, voidR);
            g.addColorStop(0, "rgba(11,11,10,0.95)");
            g.addColorStop(0.5, "rgba(11,11,10,0.7)");
            g.addColorStop(0.75, "rgba(11,11,10,0.3)");
            g.addColorStop(1, "rgba(11,11,10,0)");
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);

            // Top edge fade only
            const ef = 80;
            const tG = ctx.createLinearGradient(0, 0, 0, ef);
            tG.addColorStop(0, "rgba(11,11,10,1)");
            tG.addColorStop(1, "rgba(11,11,10,0)");
            ctx.fillStyle = tG;
            ctx.fillRect(0, 0, w, ef);

            animId = requestAnimationFrame(draw);
        }

        animId = requestAnimationFrame(draw);

        return () => {
            window.removeEventListener("resize", resize);
            cancelAnimationFrame(animId);
        };
    }, [density]);

    return (
        <canvas
            ref={canvasRef}
            className="matrix-hero__canvas"
            aria-hidden="true"
        />
    );
};

/* ═══════════════ PUBLIC API ═══════════════ */

export interface MatrixHeroProps {
    /** Main heading */
    title: string;
    /** Smaller subtitle under the heading */
    subtitle?: string;
    /** 0–1 — fraction of columns that render rain (default 0.5) */
    density?: number;
}

export const MatrixHero: React.FC<MatrixHeroProps> = ({
    title,
    subtitle,
    density = 0.5,
}) => {
    return (
        <div className="matrix-hero" role="banner">
            <div className="matrix-hero__bg" aria-hidden="true" />
            <MatrixRainCanvas density={density} />
            <div className="matrix-hero__glow" aria-hidden="true" />

            <div className="matrix-hero__content">
                <h1 className="matrix-hero__title">{title}</h1>
                {subtitle && (
                    <p className="matrix-hero__subtitle">{subtitle}</p>
                )}
                <span className="matrix-hero__line" aria-hidden="true" />
            </div>
        </div>
    );
};

export default MatrixHero;
